// Utility to format AI explanation JSON into human-readable content
export const formatAIExplanation = (aiExplanation) => {
  if (!aiExplanation) return null;

  // Log for debugging
  console.log('Raw AI Explanation:', typeof aiExplanation, aiExplanation);

  try {
    // If it's already a string that doesn't look like JSON, return as is
    if (typeof aiExplanation === 'string' && !aiExplanation.trim().startsWith('{')) {
      return {
        overall_interpretation: aiExplanation,
        raw_content: true
      };
    }

    // Parse JSON if it's a string
    let explanation;
    if (typeof aiExplanation === 'string') {
      // Clean the string first - remove any extra whitespace and ensure it's proper JSON
      const cleaned = aiExplanation.trim();
      explanation = JSON.parse(cleaned);
    } else {
      explanation = aiExplanation;
    }

    // If it's not an object, return as string
    if (typeof explanation !== 'object' || explanation === null) {
      return {
        overall_interpretation: String(explanation),
        raw_content: true
      };
    }

    console.log('Successfully parsed AI explanation:', explanation);
    return explanation;
    
  } catch (error) {
    console.warn('Failed to parse AI explanation:', error);
    
    // Try to handle truncated JSON by attempting partial parsing
    if (typeof aiExplanation === 'string' && aiExplanation.includes('{')) {
      try {
        // Try to find the last complete object by looking for the last }
        const lastBraceIndex = aiExplanation.lastIndexOf('}');
        if (lastBraceIndex > 0) {
          const truncatedJson = aiExplanation.substring(0, lastBraceIndex + 1);
          const parsed = JSON.parse(truncatedJson);
          console.log('Successfully parsed truncated JSON');
          return parsed;
        }
      } catch (partialError) {
        console.warn('Partial parsing also failed:', partialError);
      }
    }
    
    // If all parsing fails, return as formatted string
    return {
      overall_interpretation: String(aiExplanation),
      raw_content: true
    };
  }
};

export const AIExplanationDisplay = ({ explanation }) => {
  const formatted = formatAIExplanation(explanation);

  if (!formatted) {
    return <p className="text-gray-500 italic">No AI explanation available</p>;
  }

  // If it's a string, display it directly
  if (typeof formatted === 'string') {
    return (
      <div className="ai-explanation">
        <div className="prose prose-sm max-w-none">
          {formatted.split('\n').map((line, index) => (
            <p key={index} className="mb-2">
              {line}
            </p>
          ))}
        </div>
      </div>
    );
  }

  // If it's an object, format it nicely
  return (
    <div className="ai-explanation space-y-4">

      {/* For raw content, display it simply */}
      {formatted.raw_content && (
        <div className="section">
          <div className="prose prose-sm max-w-none">
            {formatted.overall_interpretation.split('\n').map((line, index) => (
              <p key={index} className="mb-2 text-gray-700">
                {line}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Regular structured content */}
      {!formatted.raw_content && formatted.overall_interpretation && (
        <div className="section">
          <h4 className="font-semibold text-purple-700 mb-2">Overall Interpretation</h4>
          <div className="prose prose-sm max-w-none">
            {formatted.overall_interpretation.split('\n').map((line, index) => (
              <p key={index} className="mb-2 text-gray-700">
                {line}
              </p>
            ))}
          </div>
        </div>
      )}

      {!formatted.raw_content && formatted.story_through_cards && (
        <div className="section">
          <h4 className="font-semibold text-purple-700 mb-2">Story Through Cards</h4>
          <div className="prose prose-sm max-w-none">
            {formatted.story_through_cards.split('\n').map((line, index) => (
              <p key={index} className="mb-2 text-gray-700">
                {line}
              </p>
            ))}
          </div>
        </div>
      )}

      {!formatted.raw_content && formatted.card_meanings && Array.isArray(formatted.card_meanings) && (
        <div className="section">
          <h4 className="font-semibold text-purple-700 mb-2">Individual Card Meanings</h4>
          <div className="space-y-3">
            {formatted.card_meanings.map((card, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                {card.position && (
                  <h5 className="font-medium text-purple-600 mb-1">{card.position}</h5>
                )}
                {card.card && (
                  <p className="font-medium text-gray-800 mb-1">{card.card}</p>
                )}
                {card.meaning && (
                  <p className="text-gray-700 text-sm">{card.meaning}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!formatted.raw_content && formatted.guidance && (
        <div className="section">
          <h4 className="font-semibold text-purple-700 mb-2">Guidance</h4>
          <div className="prose prose-sm max-w-none">
            {formatted.guidance.split('\n').map((line, index) => (
              <p key={index} className="mb-2 text-gray-700">
                {line}
              </p>
            ))}
          </div>
        </div>
      )}

      {!formatted.raw_content && formatted.reflection_questions && Array.isArray(formatted.reflection_questions) && (
        <div className="section">
          <h4 className="font-semibold text-purple-700 mb-2">Reflection Questions</h4>
          <ul className="list-disc list-inside space-y-1">
            {formatted.reflection_questions.map((question, index) => (
              <li key={index} className="text-gray-700 text-sm">
                {question}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Fallback for any other fields */}
      {!formatted.raw_content && Object.entries(formatted).map(([key, value]) => {
        // Skip already rendered fields and internal flags
        if (['overall_interpretation', 'story_through_cards', 'card_meanings', 'guidance', 'reflection_questions', 'parse_error', 'parse_warning', 'raw_content'].includes(key)) {
          return null;
        }

        return (
          <div key={key} className="section">
            <h4 className="font-semibold text-purple-700 mb-2 capitalize">
              {key.replace(/_/g, ' ')}
            </h4>
            <div className="prose prose-sm max-w-none">
              <p className="text-gray-700">
                {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};
